import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Col, Row, Form } from 'antd';
import { useTranslation } from 'react-i18next';
import { parsePhoneNumberFromString } from 'libphonenumber-js/max';
import { toLower } from 'lodash';

import InputComponent from '@/components/Input';
import { getCurrentLanguage } from '@/utils/locale';
import { validateEmail, validatePhoneNumber } from '@/utils/validate';
import InputPhone from '@/components/InputPhone';
import CheckboxInput from '@/components/Checkbox';
import ButtonComponent from '@/components/Button';
import { ProfileService } from '@/services';
import SelectFetching from '@/components/SelectFetching';

import '../index.scss';

function FormCustomerInfo({ onCheckout, formData }) {
  const [countryCode, setCountryCode] = useState('66');
  const [country, setCountry] = useState();
  const [isStoreBillingInfo, setIsStoreBillingInfo] = useState(
    !!formData.isStoreBillingInfo,
  );
  const translation = useTranslation().t;
  const [wardData, setWardData] = useState([]);
  const lang = getCurrentLanguage();

  const onFinish = (e) => {
    const cusPhone = `${countryCode}${e.customerPhone}`;
    onCheckout({
      ...e,
      customerPhone: cusPhone,
      isStoreBillingInfo,
    });
  };

  const handleSaveInfo = (e) => {
    setIsStoreBillingInfo(e.target.checked);
  };

  const onChangeCountryCode = (code) => {
    setCountryCode(code);
  };

  const handleOpenLink = (url) => {
    window.open(url);
  };

  const [formInstance] = Form.useForm();

  const [triggerFetching, setTriggerFetching] = useState([]);
  const [cusCountry, setCusCountry] = useState({});
  const fetchCountry = async (name) => {
    const { success, data } = await ProfileService.getCountry({ name });
    if (success) {
      const { results } = data;
      return results.map((o) => ({
        label: o.name,
        value: o.id,
      }));
    }
    return [];
  };

  const [cusCity, setCusCity] = useState({});
  const fetchCity = async (name) => {
    const { success, data } = await ProfileService.getProvince({
      name,
      countryId: cusCountry.value,
    });
    if (success) {
      const { results } = data;
      return results.map((o) => ({
        label: o.name,
        value: o.id,
      }));
    }
    return [];
  };

  const [cusDistrict, setCusDistrict] = useState({});
  const fetchDistrict = async (name) => {
    const { success, data } = await ProfileService.getDistrict({
      name,
      provinceId: cusCity.value,
    });
    if (success) {
      const { results } = data;
      return results.map((o) => ({
        label: o.name,
        value: o.id,
      }));
    }
    return [];
  };

  const [cusSubDistrict, setCusSubDistrict] = useState({});
  const fetchSubDistrict = async (name) => {
    const { success, data } = await ProfileService.getWard({
      name,
      districtId: cusDistrict.value,
    });
    if (success) {
      const { results } = data;
      setWardData(results);
      return results.map((o) => ({
        label: o.name,
        value: o.id,
      }));
    }
    return [];
  };

  const triggerFetchData = (fields) => {
    setTriggerFetching(fields);

    setTimeout(() => {
      setTriggerFetching([]);
    }, 50);
  };

  useEffect(() => {
    triggerFetchData(['country', 'city', 'district', 'sub_district']);
  }, []);

  useEffect(() => {
    const parsedPhone = parsePhoneNumberFromString(
      `+${formData.customerPhone}`,
    );
    let formValues = { ...formData };
    if (parsedPhone && parsedPhone.isValid()) {
      setCountry(toLower(parsedPhone.country));
      setCountryCode(parsedPhone.countryCallingCode);
      formValues = {
        ...formValues,
        customerPhone: parsedPhone.nationalNumber,
      };
    }

    formInstance.setFieldsValue({ ...formValues });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const isFetchingField = (field) => triggerFetching.includes(field);

  const handleOnChangeSubDistrict = (newValue) => {
    setCusSubDistrict({
      label: newValue.label,
      value: newValue.value,
    });
    const zipcode = wardData.find(
      (item) => item.id === newValue.value,
    )?.zipcode;

    formInstance.setFieldValue('customerZipcode', zipcode);
  };

  return (
    <Form
      form={formInstance}
      name="customer-info-form"
      layout="vertical"
      onFinish={onFinish}
      autoComplete="off"
    >
      <Row gutter={[16, 24]}>
        <Col lg={{ span: 12 }} span={24}>
          <Form.Item
            name="customerFirstName"
            rules={[
              {
                required: true,
                message: translation('sign_up.form.errors.first_name.required'),
              },
            ]}
          >
            <InputComponent
              placeholder={translation('sign_up.form.placeholder.first_name')}
              label={translation('sign_up.form.fields.first_name')}
            />
          </Form.Item>
        </Col>
        <Col lg={{ span: 12 }} span={24}>
          <Form.Item
            name="customerLastName"
            rules={[
              {
                required: true,
                message: translation('sign_up.form.errors.last_name.required'),
              },
            ]}
          >
            <InputComponent
              placeholder={translation('sign_up.form.placeholder.last_name')}
              label={translation('sign_up.form.fields.last_name')}
            />
          </Form.Item>
        </Col>
        <Col lg={{ span: 12 }} span={24}>
          <Form.Item
            name="customerEmail"
            rules={[
              {
                required: true,
                message: translation('login.form.errors.email.required'),
              },
              () => ({
                validator(_, value) {
                  if (value && !validateEmail(value)) {
                    return Promise.reject(
                      new Error(
                        translation('login.form.errors.email.not_valid'),
                      ),
                    );
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <InputComponent
              placeholder={translation('login.form.placeholder.email')}
              label={translation('login.form.fields.email')}
            />
          </Form.Item>
        </Col>
        <Col lg={{ span: 12 }} span={24}>
          <Form.Item
            name="customerPhone"
            rules={[
              {
                required: true,
                message: translation(
                  'sign_up.form.errors.phone_number.required',
                ),
              },
              () => ({
                validator(_, value) {
                  if (value && !validatePhoneNumber(countryCode, value)) {
                    return Promise.reject(
                      new Error(
                        translation('sign_up.form.errors.phone_number.invalid'),
                      ),
                    );
                  }

                  return Promise.resolve();
                },
              }),
            ]}
          >
            <InputPhone
              onChangeCountryCode={onChangeCountryCode}
              placeholder={translation('sign_up.form.placeholder.phone_number')}
              label={translation('sign_up.form.fields.phone_number')}
              country={country}
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            name="customerTaxNumber"
            rules={[
              {
                required: true,
                message: translation(
                  'customer_info.form.errors.tax_number.required',
                ),
              },
              () => ({
                validator(_, value) {
                  if (value && value.length !== 13) {
                    return Promise.reject(
                      new Error(
                        translation(
                          'customer_info.form.errors.tax_number.not_valid',
                        ),
                      ),
                    );
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <InputComponent
              type="number"
              placeholder={translation(
                'customer_info.form.placeholder.tax_number',
              )}
              label={translation('customer_info.form.fields.tax_number')}
              info={translation('customer_info.form.info.tax_number')}
            />
          </Form.Item>
        </Col>
        <Col lg={{ span: 12 }} span={24}>
          <Form.Item
            name="customerCountry"
            rules={[
              {
                required: true,
                message: translation(
                  'customer_info.form.errors.country.required',
                ),
              },
            ]}
          >
            <SelectFetching
              isDefault
              form={formInstance}
              name="customerCountry"
              triggerFetching={isFetchingField('country')}
              label={translation('customer_info.form.fields.country')}
              placeholder={translation(
                'customer_info.form.placeholder.country',
              )}
              fetchOptions={fetchCountry}
              value={cusCountry}
              onChange={(newValue) => {
                setCusCountry({
                  label: newValue.label,
                  value: newValue.value,
                });
                triggerFetchData('city');
              }}
            />
          </Form.Item>
        </Col>
        <Col lg={{ span: 12 }} span={24}>
          <Form.Item
            name="customerCity"
            rules={[
              {
                required: true,
                message: translation('customer_info.form.errors.city.required'),
              },
              () => ({
                validator(_, selectedOption) {
                  const { label, value } = selectedOption;
                  if (!label && !value) {
                    return Promise.reject(
                      new Error(
                        translation('customer_info.form.errors.city.required'),
                      ),
                    );
                  }

                  return Promise.resolve();
                },
              }),
            ]}
          >
            <SelectFetching
              triggerFetching={isFetchingField('city')}
              label={translation('customer_info.form.fields.city')}
              placeholder={translation('customer_info.form.placeholder.city')}
              fetchOptions={fetchCity}
              value={cusCity}
              onChange={(newValue) => {
                setCusCity({
                  label: newValue.label,
                  value: newValue.value,
                });
                triggerFetchData('district');
              }}
            />
          </Form.Item>
        </Col>
        <Col lg={{ span: 12 }} span={24}>
          <Form.Item
            name="customerDistrict"
            rules={[
              {
                required: true,
                message: translation(
                  'customer_info.form.errors.district.required',
                ),
              },
              () => ({
                validator(_, selectedOption) {
                  const { label, value } = selectedOption;
                  if (!label && !value) {
                    return Promise.reject(
                      new Error(
                        translation(
                          'customer_info.form.errors.district.required',
                        ),
                      ),
                    );
                  }

                  return Promise.resolve();
                },
              }),
            ]}
          >
            <SelectFetching
              triggerFetching={isFetchingField('district')}
              label={translation('customer_info.form.fields.district')}
              placeholder={translation(
                'customer_info.form.placeholder.district',
              )}
              fetchOptions={fetchDistrict}
              value={cusDistrict}
              onChange={(newValue) => {
                setCusDistrict({
                  label: newValue.label,
                  value: newValue.value,
                });
                triggerFetchData('sub_district');
              }}
            />
          </Form.Item>
        </Col>
        <Col lg={{ span: 12 }} span={24}>
          <Form.Item
            name="customerSubDistrict"
            rules={[
              {
                required: true,
                message: translation(
                  'customer_info.form.errors.sub_district.required',
                ),
              },
              () => ({
                validator(_, selectedOption) {
                  const { label, value } = selectedOption;
                  if (!label && !value) {
                    return Promise.reject(
                      new Error(
                        translation(
                          'customer_info.form.errors.sub_district.required',
                        ),
                      ),
                    );
                  }

                  return Promise.resolve();
                },
              }),
            ]}
          >
            <SelectFetching
              triggerFetching={isFetchingField('sub_district')}
              label={translation('customer_info.form.fields.sub_district')}
              placeholder={translation(
                'customer_info.form.placeholder.sub_district',
              )}
              fetchOptions={fetchSubDistrict}
              value={cusSubDistrict}
              onChange={handleOnChangeSubDistrict}
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            name="customerZipcode"
            rules={[
              {
                required: true,
                message: translation(
                  'customer_info.form.errors.zip_code.required',
                ),
              },
            ]}
          >
            <InputComponent
              label={translation('customer_info.form.fields.zip_code')}
              placeholder={translation(
                'customer_info.form.placeholder.zip_code',
              )}
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            name="customerAddress"
            rules={[
              {
                required: true,
                message: translation(
                  'customer_info.form.errors.address.required',
                ),
              },
              {
                max: 150,
                message: translation(
                  'customer_info.form.errors.address.max_length',
                ),
              },
            ]}
          >
            <InputComponent
              label={translation('customer_info.form.fields.address')}
              placeholder={translation(
                'customer_info.form.placeholder.address',
              )}
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <CheckboxInput onChange={handleSaveInfo} checked={isStoreBillingInfo}>
            {translation('customer_info.form.fields.save_info')}
          </CheckboxInput>
        </Col>
      </Row>
      <div className="form-customer__group-btn">
        <p className="mb-0">
          {translation('subscribe_customer_info.by_clicking', { text: 'our' })}
          <span
            role="button"
            aria-hidden="true"
            className="form-customer__link"
            onClick={() => handleOpenLink(process.env.REACT_APP_TERM_URL)}
          >
            {translation('subscribe_customer_info.terms')}
          </span>
          {translation('subscribe_customer_info.and')}
          <span
            role="button"
            aria-hidden="true"
            className="form-customer__link"
            onClick={() => handleOpenLink(process.env.REACT_APP_PRIVACY_URL)}
          >
            {translation('subscribe_customer_info.policy')}
          </span>
          {lang === 'th' ? translation('subscribe_customer_info.our') : ''}
        </p>
        <ButtonComponent
          type="primary"
          className="form-customer__submit-btn"
          htmlType="submit"
        >
          {translation('customer_info.form.btn_submit')}
        </ButtonComponent>
      </div>
    </Form>
  );
}

FormCustomerInfo.propTypes = {
  onCheckout: PropTypes.func,
  formData: PropTypes.object,
};

export default FormCustomerInfo;
